import { useState, useMemo, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { scrollToTop } from '@/utils/helpers';
import { SHOWED_SECTIONS_COUNT } from '@/utils/static/kyc';

export const useEntityNavigation = ({
  sections = null,
  rejectedSectionsIndexes,
  onFinish,
  onStepChange
}) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [currentSection, setCurrentSection] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [initialStep, setInitialStep] = useState(0);

  useEffect(() => {
    if (rejectedSectionsIndexes && rejectedSectionsIndexes.length > 0) {
      setCurrentSection(rejectedSectionsIndexes[0]);
      setInitialStep(rejectedSectionsIndexes[0]);
    }
  }, [rejectedSectionsIndexes]);

  const stepsCount = useMemo(() => {
    if (rejectedSectionsIndexes && rejectedSectionsIndexes.length > 0) {
      return rejectedSectionsIndexes.length;
    }
    if (sections && Array.isArray(sections) && sections.length > 0) {
      return Math.ceil(sections.length / SHOWED_SECTIONS_COUNT);
    }
    return 0;
  }, [sections, rejectedSectionsIndexes]);

  const onBack = useCallback(() => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    } else {
      state && state.prevPath ? navigate(state.prevPath, state.nextState) : navigate(-1);
    }
  }, [currentSection, state, navigate]);

  const finish = async (additionalParams = {}) => {
    if (onFinish) {
      await onFinish(additionalParams);
    }
  };

  const nextStep = useCallback(
    async (additionalParams = {}) => {
      if (rejectedSectionsIndexes) {
        const nextStepIndex = currentStep + 1;
        const stepsLength = rejectedSectionsIndexes.length;

        if (nextStepIndex < stepsLength) {
          setCurrentStep(nextStepIndex);
          const nextSectionIndex = rejectedSectionsIndexes[nextStepIndex];
          setCurrentSection(nextSectionIndex);
          setInitialStep(nextSectionIndex);
          if (onStepChange) {
            onStepChange();
          }
        } else {
          await finish(additionalParams);
        }
      }

      if (!rejectedSectionsIndexes) {
        if (currentSection + 1 < stepsCount) {
          setCurrentSection(currentSection + 1);
          if (currentSection >= initialStep) setInitialStep(currentSection + 1);
          if (onStepChange) {
            onStepChange();
          }
        } else {
          await finish(additionalParams);
        }
      }

      scrollToTop({ behavior: 'smooth' });
    },
    [
      currentSection,
      currentStep,
      rejectedSectionsIndexes,
      stepsCount,
      initialStep,
      onFinish,
      onStepChange
    ]
  );

  return {
    currentSection,
    setCurrentSection,
    currentStep,
    setCurrentStep,
    initialStep,
    setInitialStep,
    stepsCount,
    onBack,
    nextStep,
    finish
  };
};
